import {useFlux} from '@nlabs/arkhamjs-utils-react';
import {getGraphql} from '@nlabs/metropolisjs/utils';
import {useEffect} from 'react';

import {
  parseSentinelArchiveResponse,
  publicArticleEndpoint,
  SENTINEL_ARCHIVE_QUERY
} from '../services/articles/reaktorArticleClient';
import {ArticleLink} from '../components/ArticleLink';
import {SiteFooter} from '../components/SiteFooter';
import {SiteHeader} from '../components/SiteHeader';
import {useStoreState} from '../stores/useStoreState';
import type {Article} from '../types/article';

export const ArchivePage = () => {
  const flux = useFlux();
  const liveArticles = useStoreState<Article[]>('homepage.liveArticles', []);
  const feedStatus = useStoreState<'error' | 'live' | 'loading'>('homepage.status', 'loading');
  const archivedArticles = liveArticles;

  useEffect(() => {
    if(feedStatus !== 'live') return;
    let active = true;

    const loadArchive = async () => {
      const collected: Article[] = [];
      let offset = 0;
      const pageSize = 100;

      while(active) {
        const data = await getGraphql(
          flux,
          publicArticleEndpoint(window.location.hostname),
          false,
          {
          query: SENTINEL_ARCHIVE_QUERY,
          variables: {from: offset, to: offset + pageSize}
          },
          {}
        );
        const page = parseSentinelArchiveResponse({data} as Parameters<typeof parseSentinelArchiveResponse>[0]);
        collected.push(...page);
        if(page.length < pageSize) break;
        offset += pageSize;
      }

      if(active) void flux.dispatch({articles: collected, status: 'live', type: 'ARTICLE_FEED_REPLACE'});
    };

    void loadArchive().catch(() => {
      // Keep the already loaded article pool when archive pagination fails.
    });
    return () => { active = false; };
  }, [feedStatus, flux]);

  return (
    <div className="newspaper">
      <SiteHeader />
      <main className="archive-page shell" id="main-content">
        <header className="archive-header">
          <span className="eyebrow">Chicago Sentinel Times</span>
          <h1>Article Archive</h1>
          <p>Stored editions of attributed reporting and clearly labeled Sentinel editorials.</p>
        </header>
        {archivedArticles.length ? (
          <div className="archive-list">
            {archivedArticles.map((article) => (
              <article key={article.id}>
                <time dateTime={article.publishedAt}>{new Date(article.publishedAt).toLocaleDateString('en-US')}</time>
                <div>
                  <span>{article.section} · {article.sourceName}</span>
                  <h2><ArticleLink article={article}>{article.headline}</ArticleLink></h2>
                  <p>{article.description}</p>
                </div>
              </article>
            ))}
          </div>
        ) : <p className="archive-empty">The archive will fill as additional sourced articles are retrieved.</p>}
      </main>
      <SiteFooter />
    </div>
  );
};
